import { DestroyRef, computed, effect, EffectRef, inject, Injectable, Signal, signal, WritableSignal } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { MenuModes } from "@models/enums/components/Menu/MenuModes.enum";
import { MenuTypes } from "@models/enums/components/Menu/MenuTypes.enum";
import { GroupAssignedAmenity } from "@models/interfaces/amenities/AmenityGroupAssigned.interface";
import { AmenityNode } from "@models/interfaces/amenities/AmenityNode.interface";
import { Menu, MenuConfiguration, MenuContent, MenuContentItem, MenuKeys } from "@models/interfaces/components/Menu.interface";
import { Configuration } from "@models/interfaces/configuration/index.interface";
import { AmenitiesService } from "@services/amenities.service";
import { DvmService } from "@services/dvm.service";
import { UiService } from "@services/ui.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { flattenMenuContent } from "./menu-content-tree.util";

@Injectable()
export class MenuFactoryService {

    // SERVICES
    private readonly config:        Signal<Configuration['Ui']>                = inject(UiService).uiConfig;
    private readonly ams:           AmenitiesService                           = inject(AmenitiesService);
    private readonly dvm:           DvmService                                 = inject(DvmService);
    private readonly destroyRef:    DestroyRef                                 = inject(DestroyRef);

    // CONFIGURATION
    private readonly menuConfig:    Signal<Configuration['Ui']['Menus']>   = computed((): MenuConfiguration => this.config().Menus);

    // STATE
    public  readonly menus:         Map<MenuKeys, Menu>                    = new Map<MenuKeys, Menu>();
    public  readonly menusList:     WritableSignal<Menu[]>                 = signal<Menu[]>([]);
    public  readonly menusReady:    WritableSignal<number>                 = signal<number>(0);
    private loadId:                 number                                 = 0;

    // EFFECTS
    private readonly configChange:  EffectRef                              = effect( async (): Promise<void> => {

        const loadId: number = ++this.loadId;

        // Clear existing menus before setting new ones
        this.menus.clear();
        this.menusList.set([]);

        const hasMenuContent: boolean = Object.values(this.menuConfig().menus ?? {}).some(menu => menu?.content?.length);

        if (hasMenuContent) await this.setMenus(loadId);
        else if (loadId === this.loadId) this.menusReady.update((version: number): number => version + 1);

    });

    /**
     * Registra la inicializacion de tags de menu despues de cada carga correcta del mapa.
     */
    constructor() {
        this.dvm.viewer.getObservable('load_success')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((): void => this.initMenuTags());
    }

    // PUBLIC METHODS

    /**
     * Método que inicializa los tags y los grupos de los nodos del mapa segun el contenido de los menús
     */
    public initMenuTags(): void {

        this.menus.forEach((menu: Menu): void => this.setTagsByMenu(menu));

    }

    /**
     * Método que crea un objeto de tipo MenuContentItem a partir de un objeto de tipo GroupAssignedAmenity.
     * 
     * @param amenity Amenity asignado que se adapta al formato comun de menu.
     * @returns Un objeto de tipo MenuContentItem.
     */
    public createMenuContentItem(amenity: GroupAssignedAmenity): MenuContentItem {
        
        const itemContent: MenuContentItem = {
            id:     amenity.id,
            title:  amenity.title,
            type:   MenuTypes.premium,
            mode:   MenuModes.filter,
        };

        Object.assign(itemContent, amenity);

        if (amenity.nodes?.length) itemContent.relatedSeats = this.parseRelatedSeats(amenity.nodes);
        
        return itemContent;

    }

    // PRIVATE METHODS

    //#region MENU SETTER
    
    /**
     * Método que establece los menús en el servicio.
     * 
     * Recorre la configuración de menús y crea un array de promsesas para procesar cada menú de manera asíncrona.
     * Esto es porque en el sistema de menús pueden haber menús que vengan informados de manera manual y otros que vengan por api.
     * Para estandarizar las interfaces, tanto del menu normal como el de ams, se procesan los datos de configuración de manera asíncrona, 
     * así se garantiza que todos los menús esten disponibles a la vez y que el mapa de menús (this.menus) no se modifique 
     * en diferentes momentos, ya que es donde los componentes consumen los menus y provocarian repintados en el DOM innecesarios.
     * 
     * Nota: si el elemento no es asincrono (viene dado de manera manual), aunque sea una promesa asincrona, se resolverá inmediatamente. 
     * 
     * Para cada menú se realiza un proceso que envuelve en una promesa los siguientes pasos:
     * 1. Se comprueba si el menú contiene contenido de tipo AMS. 
     * 2. Si tiene, se crea un menú especifico para AMS, si no se añade al mapa de menús directamente.
     * 3. Como el menu AMS proviene de una api, debemos esperar a que se resuelva.
     * 4. Una vez resuelto, se estandariza el contenido a una interfaz común para todos los menús. 
     * 5. Finalmente, se añade el menú al mapa de menús (this.menus).
     *
     * Una vez que todos los menús se han procesado y guardado en un array de promesas, se espera a que todas las promesas se resuelvan.
     *
     * @param loadId Identificador de la carga vigente, usado para descartar resultados obsoletos.
     */
    private async setMenus(loadId: number): Promise<void> {

        const menus:        Record<MenuKeys,Menu|undefined> = this.menuConfig().menus ?? ({} as Record<MenuKeys, Menu | undefined>),
              menuEntries:  [string, Menu | undefined][]    = Object.entries(menus),
              nextMenus:    Map<MenuKeys, Menu>             = new Map<MenuKeys, Menu>(),
              menuPromises: Promise<void>[]                 = menuEntries.map(async ([key, menu]): Promise<void> => this.createMenu(nextMenus, key as MenuKeys, menu))
              
        await Promise
            .allSettled(menuPromises)
            .then((): void => {

                if (loadId !== this.loadId) return;

                this.menus.clear();
                nextMenus.forEach((menu: Menu, key: MenuKeys): void => {
                    this.menus.set(key, menu);
                });

                // Indicamos que el mapa de menús ya está cargado al subject
                this.ams.amsMapLoaded$.next(true);

                // Notify reactive consumers (template) that the menus Map is now populated
                this.menusList.set(Array.from(this.menus.values()));
                this.menusReady.update((version: number): number => version + 1);

            })
    
    }

    /**
     * Método que crea un menú y lo añade al mapa de menús.
     * 
     * Comprueba si el menú tiene contenido de tipo AMS, si lo tiene, crea un menú específico para AMS.
     * Si no lo tiene, añade el menú directamente al mapa de menús.
     * 
     * @param target Mapa temporal donde se acumulan los menus construidos.
     * @param key Clave del menu a crear.
     * @param menu Configuracion del menu a crear.
     * 
     */
    private async createMenu(target: Map<MenuKeys, Menu>, key:string, menu?: Menu): Promise<void> {
        
        if (!menu?.content?.length) return;

        const nextMenu:      Menu    = this.cloneMenu(menu),
              hasAmsContent: boolean = nextMenu.content.some((item) => item.amsId);

        if(hasAmsContent) await this.createAmsMenu(target, key, nextMenu)

        else target.set(key as MenuKeys, nextMenu);
        
    }

    /**
     * Método que crea un menú específico para AMS.
     * 
     * Como los menus pueden mezclar contenido de tipo AMS y contenido normal, separamos el contenido en dos arrays:
     *  1. amsContent: Contenido de tipo AMS.
     *  2. regularContent: Contenido normal.
     * 
     * En los menus de tipo AMS, el contenido es un id de la api de AMS, por lo que obtenemos los ids (amsIds) de cada item del contenido AMS
     * y hacemos una llamada a la api de AMS para obtener los datos de los amenities.
     * 
     * Una vez obtenidos los amenities, se itera sobre el contenido AMS y se sobreescribe el campo data de cada item por un objeto de tipo MenuContentItem.
     * De esta manera, se estandariza el contenido de los menús de tipo AMS con el contenido normal.
     * 
     * Finalmente, se une el contenido AMS con el contenido normal y se añade al mapa de menús.
     * 
     * @param target Mapa temporal donde se guarda el menu resultante.
     * @param key Clave del menu que define el tipo final de sus items.
     * @param menu Menu con contenido AMS ya clonado para completar con datos de API.
     */
    private async createAmsMenu(target: Map<MenuKeys, Menu>, key: string, menu: Menu): Promise<void> {

        const   amsContent:      MenuContent[]                  = menu.content.filter((item) => item.amsId),                // Array de MenuContents que tengan un amsId.
                regularContent:  MenuContent[]                  = menu.content.filter((item) => !item.amsId),               // Array del resto de MenuContents que no tengan un amsId.
                amsIds:          string[]                       = amsContent.map((item) => item.amsId!.toString()),         // Array de ids de amsContent 
                hasData:         (item: MenuContent) => boolean = (item): boolean => Object.keys(item.data).length > 0,     // Helper para comprobar si un item tiene datos.
                amenities:       GroupAssignedAmenity[]         = await firstValueFrom(this.ams.searchAmenities(amsIds));   // Llamada a la api de AMS para obtener los datos de los amenities.

        // Si la llave es premium, pasamos los regular items a type premium
        if(key !== MenuTypes.regular) regularContent.forEach(item => item.data.type = key as MenuTypes);

        // Iteramos sobre los amenities obtenidos y sobreescribimos el objeto data del item que coincida con el id del amenity.
        amenities.forEach((amenity: GroupAssignedAmenity)=> {

            this.ams.amsMap.set(amenity.id, amenity); // Añadimos el amenity al mapa de amenities del servicio AMS.

            const item = amsContent.find(item => item.amsId === amenity.id);

            if (item) {
                item.data = this.createMenuContentItem(amenity);
                item.data.type = key === MenuTypes.regular ? MenuTypes.regular : MenuTypes.premium;
            }

        })

        // Comprobamos si el campo data de algun item de amsContent no tiene datos, si es asi lo eliminamos. 
        if(amsContent.some(item => !hasData(item))) { 

            // Si hay algun item que no cumple con hasData, ejecutamos splice y devolvemos false para que no se incluya en el array.
            amsContent.filter((item,index,arr) => hasData(item) || (arr.splice(index, 1), false));

        }

        // Ahora que tenemos el contenido AMS estandarizado, lo unimos con el contenido regular y reemplazamos el contenido del menú.
        // Join regularContent with amsContent but preserving original order
        menu.content = menu.content
          .map(item => {
            if (item.amsId) {
                return amsContent.find(amsItem => amsItem.amsId === item.amsId);
            } else {
                return regularContent.find(regItem => regItem === item);
            }
          })
          .filter(item => item != null) as MenuContent[];

        // Finalmente, añadimos el menú al mapa de menús.
        target.set(key as MenuKeys, menu);
    }

    /**
     * Crea una copia profunda de un menu antes de completar contenido AMS o tags.
     *
     * @param menu Menu original de la configuracion.
     * @returns Copia independiente del menu.
     */
    private cloneMenu(menu: Menu): Menu {

        return structuredClone(menu);

    }

    /**
     * Método que parsea los nodos de un amenity a un objeto de tipo MenuContentItem['relatedSeats'].
     *
     * @param nodes - Array de AmenityNode con los nodos relacionados.
     * @returns Un objeto de tipo MenuContentItem['relatedSeats'].
     */
    private parseRelatedSeats(nodes: AmenityNode[]): MenuContentItem['relatedSeats'] {

        const seatIds = nodes.map((n: AmenityNode) => n.name);

        const relatedSeats: MenuContentItem['relatedSeats'] = {
            section:   [],
            row:       [],
            seat:      seatIds,
        };

        const sectionSet = new Set<string>(),
              rowSet     = new Set<string>();

        seatIds.forEach((id: string) => {

            sectionSet.add(id.split('-')[0]);
            rowSet.add(id.split('-').slice(0, -1).join('-'));

        });

        relatedSeats.section = Array.from(sectionSet);
        relatedSeats.row     = Array.from(rowSet);

        return relatedSeats;

    }

    /**
     * Metodo que establece los tags y grupos a los nodos del mapa. 
     * Este método recibe un menú y inicializa un objeto content que contiene el tipo y todos los nodos como array.
     * Finalmente, recorre el objeto content y establece los tags y grupos a los nodos del mapa.
     *
     * @param menu Menu del que se quieren establecer los tags y grupos.
     */
    private setTagsByMenu(menu: Menu): void {
        
        const content: { type: MenuTypes; nodes: string[] }[] = flattenMenuContent(menu.content)
            .filter((item: MenuContent): boolean => !!item.data?.type && !!item.data?.relatedSeats)
            .filter((item: MenuContent): boolean => item.data.type !== MenuTypes.premium || !!item.amsId)
            .map(item => ({ 
                type:   item.data.type, 
                nodes:  Object.values(item.data.relatedSeats ?? {}).flat()
            }))
            .filter((item): boolean => item.nodes.length > 0);

        content.forEach(item => ( 
            // this.dvm.viewer.setNodesTag(item.nodes, item.type),
            this.dvm.viewer.addNodesToGroup(item.nodes, item.type)
        ));

    } 

    //#endregion MENU SETTER       

}
